/**
 * 포커스된 입력창을 키보드 바로 위로 올린다.
 *
 * `[data-keyboard-aware]` 속성이 붙은 컨테이너(바텀시트 등) 안의 입력창에만 동작한다.
 * 컨테이너 자체는 `bottom: var(--keyboard-inset)`으로 이미 키보드 위에 떠 있으므로,
 * 여기서는 컨테이너 내부 스크롤 영역을 움직여 입력창의 하단을 보이는 영역의 하단에 맞춘다.
 *
 * 문서 레벨에 리스너를 하나만 두어, 시트가 열리고 닫힐 때마다 등록/해제할 필요가 없게 했다.
 */

import {
  KEYBOARD_OPEN_THRESHOLD,
  getVisibleBottom,
  getVisualViewportInsets,
  isKeyboardOpen,
  subscribeVisualViewportInsets,
  waitForLayoutSettle,
} from "./visual-viewport";

const KEYBOARD_AWARE_SELECTOR = "[data-keyboard-aware]";

/** 입력창 하단과 키보드 상단 사이 여백 */
const FOCUS_BOTTOM_GAP = 8;

/** 소프트 키보드를 띄우지 않는 input type */
const NON_TEXT_INPUT_TYPES = new Set([
  "button",
  "checkbox",
  "color",
  "file",
  "hidden",
  "image",
  "radio",
  "range",
  "reset",
  "submit",
]);

let started = false;
let alignmentToken = 0;
let lastKnownKeyboardInset = 0;

function isTextInput(node: unknown): node is HTMLElement {
  if (node instanceof HTMLTextAreaElement) return true;
  if (node instanceof HTMLInputElement) {
    return !NON_TEXT_INPUT_TYPES.has(node.type);
  }
  return node instanceof HTMLElement && node.isContentEditable;
}

function findScroller(
  target: HTMLElement,
  root: HTMLElement,
): HTMLElement | null {
  let node = target.parentElement;

  while (node) {
    const { overflowY } = getComputedStyle(node);
    if (
      (overflowY === "auto" || overflowY === "scroll") &&
      node.scrollHeight > node.clientHeight + 1
    ) {
      return node;
    }
    if (node === root) return null;
    node = node.parentElement;
  }

  return null;
}

interface AlignmentGeometry {
  /** 포커스된 입력창의 하단 y좌표 (layout viewport 기준) */
  targetBottom: number;
  /** 스크롤 영역의 하단 y좌표 */
  scrollerBottom: number;
  /** 보이는 영역의 하단 y좌표. 키보드가 열려 있으면 키보드 상단 */
  visibleBottom: number;
}

/**
 * 스크롤 영역을 얼마나 움직여야 입력창이 키보드 바로 위에 오는지 계산한다.
 * 1px 미만이면 움직이지 않는다(불필요한 스크롤 방지).
 */
export function computeAlignmentDelta({
  targetBottom,
  scrollerBottom,
  visibleBottom,
}: AlignmentGeometry): number {
  // 스크롤 영역 하단과 키보드 상단 중 더 위쪽이 실제로 보이는 하단이다.
  const bottom = Math.min(scrollerBottom, visibleBottom);
  const delta = targetBottom + FOCUS_BOTTOM_GAP - bottom;
  return Math.abs(delta) < 1 ? 0 : delta;
}

function alignAboveKeyboard(
  target: HTMLElement,
  root: HTMLElement,
  smooth: boolean,
) {
  const scroller = findScroller(target, root);
  if (!scroller) return;

  const delta = computeAlignmentDelta({
    targetBottom: target.getBoundingClientRect().bottom,
    scrollerBottom: scroller.getBoundingClientRect().bottom,
    visibleBottom: getVisibleBottom(),
  });
  if (delta === 0) return;

  // scrollBy는 스크롤 가능 범위를 벗어나면 자동으로 잘리므로 별도 clamp가 필요 없다.
  scroller.scrollBy({ top: delta, behavior: smooth ? "smooth" : "auto" });
}

/**
 * 레이아웃(뷰포트 + 시트 위치)이 안정된 뒤에 정렬한다.
 * 여러 경로에서 호출되어도 마지막 요청만 실행되도록 토큰으로 막는다.
 */
function scheduleAlignment(
  target: HTMLElement,
  root: HTMLElement,
  { expectChange, smooth }: { expectChange: boolean; smooth: boolean },
) {
  const token = (alignmentToken += 1);
  const isCancelled = () => token !== alignmentToken;

  void waitForLayoutSettle({ expectChange, element: root, isCancelled })
    .then(() => {
      if (isCancelled()) return;
      if (document.activeElement !== target) return;
      if (!isKeyboardOpen()) return;
      alignAboveKeyboard(target, root, smooth);
    })
    // 문서 레벨 전역 리스너이므로 어떤 예외도 unhandled rejection으로 새지 않게 한다.
    .catch(() => {});
}

function resolveTarget(node: unknown) {
  if (!isTextInput(node)) return null;
  const root = node.closest<HTMLElement>(KEYBOARD_AWARE_SELECTOR);
  if (!root) return null;
  return { target: node, root };
}

function handleFocusIn(event: FocusEvent) {
  const resolved = resolveTarget(event.target);
  if (!resolved) return;

  const keyboardWasOpen = isKeyboardOpen();
  scheduleAlignment(resolved.target, resolved.root, {
    // 키보드가 새로 올라오는 중이면 뷰포트가 바뀔 때까지 기다렸다가 위치를 잰다.
    expectChange: !keyboardWasOpen,
    // 키보드가 올라오는 동안에는 즉시 이동해 애니메이션이 겹치지 않게 하고,
    // 이미 열린 상태에서 필드 간 이동은 부드럽게 처리한다.
    smooth: keyboardWasOpen,
  });
}

/** 이모지/서드파티 키보드 전환 등으로 키보드 높이가 바뀌면 다시 정렬한다. */
function handleInsetsChange() {
  const { keyboardInset } = getVisualViewportInsets();
  const previous = lastKnownKeyboardInset;
  lastKnownKeyboardInset = keyboardInset;

  if (Math.abs(keyboardInset - previous) < KEYBOARD_OPEN_THRESHOLD) return;
  // 닫히는 중이면 정렬할 필요가 없다. 900ms 폴링도 시작하지 않는다.
  if (!isKeyboardOpen()) return;

  const resolved = resolveTarget(document.activeElement);
  if (!resolved) return;

  scheduleAlignment(resolved.target, resolved.root, {
    expectChange: false,
    smooth: previous > KEYBOARD_OPEN_THRESHOLD,
  });
}

/** 앱 시작 시 한 번만 호출한다. */
export function startKeyboardFocusTracking() {
  if (started || typeof document === "undefined") return;
  started = true;

  document.addEventListener("focusin", handleFocusIn);
  subscribeVisualViewportInsets(handleInsetsChange);
}
